import { useNotes, useTask } from "@hq/client-react";
import { TaskDetail } from "@hq/tasks-ui";
import { Button, Drawer, IconButton } from "@z/ui";
import { ArrowLeft, FileText } from "lucide-react";
import type { RefObject } from "react";
import { useWorkspace } from "../workspace/WorkspaceContext";

export function TaskDrawer({
  taskId,
  open,
  onClose,
  returnFocus,
}: {
  taskId: string;
  open: boolean;
  onClose: () => Promise<boolean>;
  returnFocus: RefObject<HTMLElement | null>;
}) {
  const ws = useWorkspace();
  const { data: task } = useTask(taskId);
  const { data: notes } = useNotes();
  const source = notes?.find((note) => note.id === task?.sourceNoteId);
  return (
    <Drawer.Root
      open={open}
      swipeDirection="right"
      onOpenChange={(next) => {
        if (!next) void onClose();
      }}
    >
      <Drawer.Content
        finalFocus={() =>
          returnFocus.current?.isConnected
            ? returnFocus.current
            : document.querySelector<HTMLElement>("[data-slot='task-list']")
        }
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3">
          <IconButton label="Back to list" size="sm" onClick={() => void onClose()}>
            <ArrowLeft />
          </IconButton>
          <Drawer.Title className="text-sm font-medium">To-do details</Drawer.Title>
          <Drawer.Description className="sr-only">
            Edit your to-do. Close to return to your previous view.
          </Drawer.Description>
          <Button className="ml-auto" variant="ghost" size="sm" onClick={() => void onClose()}>
            Done
          </Button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <TaskDetail
            key={taskId}
            taskId={taskId}
            className="px-5 py-5 sm:px-7"
            onDeleted={() => void onClose()}
            source={
              task?.sourceNoteId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start max-w-full text-fg-muted"
                  disabled={!source}
                  onClick={async () => {
                    if (source && (await onClose()))
                      ws.open({ paneType: "note", noteId: source.id });
                  }}
                >
                  <FileText />
                  <span className="truncate">
                    {source ? `From ${source.title}` : "Source note is in Trash or unavailable"}
                  </span>
                </Button>
              )
            }
          />
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
}
